import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { Sidebar } from '../components/Sidebar';
import { StepDetail } from '../components/StepDetail';
import { ArtifactPanel } from '../components/ArtifactPanel';
import { FullscreenOverlay } from '../components/FullscreenOverlay';
import { CascadeTimeline } from '../components/CascadeTimeline';
import { I, Progress } from '../components/Shared';
import { Shell } from '../components/Shell';
import { ShareModal } from '../components/ShareModal';
import { ActivityModal } from '../components/ActivityModal';
import {
  getFactoryConfig,
  syncProjectToConfluence,
  getProjectConfluenceProfile,
  setProjectConfluenceProfile,
  type ProjectConfluenceProfile,
  syncProjectToRepo,
  pullProjectFromRepo,
  getProjectRepoStatus,
  getProjectRepoProfile,
  setProjectRepoProfile,
  type ProjectRepoProfile,
  type RepoStatus,
} from '../lib/api';
import { toAppArchive, appFileName, downloadBlob } from '../lib/appIo';
import { isAdminRole } from '../lib/auth';
import { FactoryConfig } from '../types';

export function Pipeline() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const project = useStore((s) => s.project);
  const rpOpen = useStore((s) => s.rpOpen);
  const loadProject = useStore((s) => s.loadProject);
  const openDemo = useStore((s) => s.openDemo);
  const tokenPills = useStore((s) => s.tokenPills);
  const currentUser = useStore((s) => s.currentUser);
  const projectRole = useStore((s) => s.projectRole);
  const demoSimRunning = useStore((s) => s.demoSimRunning);
  const startDemoSim = useStore((s) => s.startDemoSimulation);
  const stopDemoSim = useStore((s) => s.stopDemoSimulation);
  const bootstrapWarning = useStore((s) => s.bootstrapWarning);

  const [cfg, setCfg] = useState<FactoryConfig | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  const isOwner = projectRole === 'owner' || (!!currentUser && isAdminRole(currentUser.role));
  const isViewer = projectRole === 'viewer';

  useEffect(() => {
    getFactoryConfig().then(setCfg).catch(() => setCfg(null));
  }, []);

  // Load project from URL param if not already loaded
  useEffect(() => {
    if (!projectId) return;
    if (projectId === 'demo') {
      // Demo project has a different internal id — only reload if no project is loaded
      if (!project) void openDemo();
      return;
    }
    if (!project || project.id !== projectId) {
      // If the project can't be loaded (e.g. it belongs to a workspace the user
      // just switched away from), don't leave an uncaught rejection / error
      // overlay — bounce back to the workspace-scoped apps list.
      void loadProject(projectId).catch(() => navigate('/apps', { replace: true }));
    }
  }, [projectId, project, loadProject, openDemo, navigate]);

  // Stop demo simulation if user navigates away
  useEffect(() => {
    return () => { if (useStore.getState().demoSimRunning) stopDemoSim(); };
  }, [stopDemoSim]);

  const totalSteps = project?.phases.reduce((n, p) => n + p.steps.length, 0) ?? 0;
  const doneSteps = project?.phases.reduce((n, p) => n + p.steps.filter((s) => s.status === 'done').length, 0) ?? 0;
  const phases = project?.phases ?? [];
  const donePhases = phases.filter((p) => p.steps.every((s) => s.status === 'done')).length;
  const nowPhase = phases.findIndex((p) => p.steps.some((s) => s.status === 'in-progress' || s.status === 'pending'));

  // Compute total cost from tokenPills
  const totalCost = Object.values(tokenPills).reduce((sum, p) => sum + (p?.costUsd ?? 0), 0);
  // Compute total pipeline time from persisted step times
  const totalTimeMs = project?.phases.reduce(
    (sum, p) => sum + p.steps.reduce((s2, st) => s2 + (st.totalTimeMs ?? 0), 0), 0,
  ) ?? 0;

  // Demo launch is gated on jira-mock-setup completing — that's the step that
  // emits the webpack config the WC build needs. The "Launch demo" button
  // shows up only when this is done; it's the predictable signal that the
  // project has a buildable shape.
  const isDemo = project ? !project.id.startsWith('p_') : false;
  const demoReady = phases.some((ph) =>
    ph.steps.some(
      (s) => (s.skillRef ?? s.key) === 'jira-mock-setup' && s.status === 'done',
    ),
  );

  return (
    <Shell active="apps">
    <div className="af" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <style>{`
        @keyframes run-slide { 0% { left: -40%; } 100% { left: 100%; } }
        .run-bar { left: -40%; }
      `}</style>

      {/* top bar */}
      <header style={{ display: 'flex', alignItems: 'center', padding: '10px 18px', borderBottom: '1px solid var(--line)', background: 'var(--surface)', gap: 14, flex: '0 0 auto' }}>
        <button className="btn btn--ghost btn--sm" onClick={() => navigate('/apps')}>{I.chevL} My Apps</button>
        <span style={{ width: 1, height: 18, background: 'var(--line)' }} />
        <span className="t-h3">{project?.appName ?? 'App'}</span>
        {project?.metadata?.product && <span className="chip">{project.metadata.product}</span>}
        <span style={{ flex: 1 }} />
        <span className="t-micro muted">
          {totalTimeMs > 0 ? formatDuration(totalTimeMs) : ''}
          {totalTimeMs > 0 && totalCost > 0 ? ' · ' : ''}
          {totalCost > 0 ? `$${totalCost.toFixed(2)}` : ''}
        </span>
        <Progress total={phases.length} done={donePhases} now={nowPhase} />
        <span className="t-num t-small muted">{doneSteps}/{totalSteps}</span>
        {isViewer && (
          <span className="chip" title="You have read-only access to this shared app">View only</span>
        )}
        {project && (
          <button
            className="btn btn--ghost btn--sm btn--icon"
            title="Export app as a .zip of real files"
            onClick={async () => {
              try {
                downloadBlob(appFileName(project.appName), await toAppArchive(project));
              } catch (e: any) {
                window.alert(`Could not export app: ${e?.message ?? 'unknown error'}`);
              }
            }}
          >
            {I.download}
          </button>
        )}
        {projectId && projectId !== 'demo' && (
          <button className="btn btn--ghost btn--sm btn--icon" title="Activity — who did what" onClick={() => setActivityOpen(true)}>{I.users}</button>
        )}
        {projectId && projectId !== 'demo' && isOwner && (
          <button className="btn btn--ghost btn--sm btn--icon" title="Share with workspace members" onClick={() => setShareOpen(true)}>{I.share}</button>
        )}
        {project && !project.id.startsWith('p_') && (
          demoSimRunning ? (
            <button className="btn btn--ghost btn--sm" onClick={stopDemoSim} style={{ color: 'var(--err)' }}>
              {I.x} Stop
            </button>
          ) : (
            <button className="btn btn--accent btn--sm" data-demo-run-pipeline onClick={startDemoSim}>
              {I.play} Run Pipeline
            </button>
          )
        )}
        {project && !demoSimRunning && (
          <ConfluenceSyncControl projectId={project.id} canEdit={isOwner} isDemo={!project.id.startsWith('p_')} />
        )}
        {project && !demoSimRunning && project.id.startsWith('p_') && (
          <GitSyncControl projectId={project.id} canEdit={isOwner} />
        )}
        {demoReady && projectId && (
          <button
            className="btn btn--primary btn--sm"
            onClick={() => navigate(`/pipeline/${projectId}/demo`)}
            title="Build and run this app's jira-mock demo in your browser"
          >
            Launch demo
          </button>
        )}
      </header>

      {/* Non-fatal bootstrap notice (e.g. LLM API not configured → name derived from idea) */}
      {bootstrapWarning && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 18px',
            background: 'var(--oa-warn-bg, #4a3a0033)',
            borderBottom: '1px solid var(--line)',
            color: 'var(--oa-warn, #b8860b)',
            flex: '0 0 auto',
          }}
        >
          <span className="t-micro" style={{ flex: 1 }}>⚠ {bootstrapWarning}</span>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => useStore.setState({ bootstrapWarning: null })}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* 3-pane */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: rpOpen ? `${isDemo ? 240 : 280}px 1fr ${isDemo ? 380 : 520}px` : `${isDemo ? 240 : 280}px 1fr`, minHeight: 0 }}>
        <Sidebar />
        <StepDetail />
        {rpOpen && <ArtifactPanel />}
      </div>
      <FullscreenOverlay />
      <CascadeTimeline />
      {shareOpen && projectId && project && (
        <ShareModal
          projectId={projectId}
          appName={project.appName}
          canManage={isOwner}
          onClose={() => setShareOpen(false)}
        />
      )}
      {activityOpen && projectId && project && (
        <ActivityModal
          projectId={projectId}
          appName={project.appName}
          onClose={() => setActivityOpen(false)}
        />
      )}
    </div>
    </Shell>
  );
}

/**
 * Consolidated Confluence-sync control: a single compact "Confluence" button in
 * the header that opens a bounded dropdown with the connection target picker and
 * a Sync action, mirroring GitSyncControl. Keeps the header uncluttered and
 * confines (long) error text to a fixed-width panel. Shown when the workspace
 * has connections (or in demo mode, where a showcase sync is always offered).
 */
function ConfluenceSyncControl({ projectId, canEdit, isDemo }: { projectId: string; canEdit: boolean; isDemo: boolean }) {
  const [data, setData] = useState<ProjectConfluenceProfile | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    getProjectConfluenceProfile(projectId)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [projectId]);

  // Hidden when there are no connections and this isn't a demo showcase.
  const hasProfiles = !!data && data.profiles.length > 0;
  if (!hasProfiles && !isDemo) return null;

  const defaultProfile = data?.profiles.find((p) => p.isDefault);
  const effective = data?.profiles.find((p) => p.id === data.effectiveProfileId);

  const onTargetChange = async (next: string): Promise<void> => {
    setBusy(true);
    try {
      const r = await setProjectConfluenceProfile(projectId, next || null);
      setData((d) => (d ? { ...d, selectedProfileId: r.selectedProfileId, effectiveProfileId: r.effectiveProfileId } : d));
      void useStore.getState().refreshProjectData(projectId);
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message ?? 'Could not change target' });
    } finally {
      setBusy(false);
    }
  };

  const sync = async (): Promise<void> => {
    setBusy(true); setMsg(null);
    try {
      // Showcase (fs-backed) demo projects resolve through the same storage +
      // sync path as real DB projects, so sync by id for both.
      const r = await syncProjectToConfluence(projectId);
      // Store Confluence page refs so the artifact panel shows links.
      useStore.setState((s) => ({ publishedDocs: { ...s.publishedDocs, ...r.publishedDocs } }));
      setMsg({ ok: true, text: `Synced ${r.published} doc${r.published === 1 ? '' : 's'}` });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message ?? 'Sync failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="btn btn--sm"
        onClick={() => setOpen((o) => !o)}
        title={effective ? `Confluence: ${effective.name}` : 'Confluence sync'}
      >
        {I.confluence} Confluence
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 41,
              width: 320, maxWidth: '90vw', padding: 12, borderRadius: 10,
              background: 'var(--surface, #fff)', border: '1px solid var(--line, #e2e2e2)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div className="t-micro" style={{ fontWeight: 600 }}>Confluence sync</div>

            {hasProfiles && canEdit ? (
              <label className="t-micro muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Connection
                <select
                  className="select select--sm"
                  value={data?.selectedProfileId ?? ''}
                  disabled={busy}
                  onChange={(e) => void onTargetChange(e.target.value)}
                >
                  <option value="">{defaultProfile ? `Default (${defaultProfile.name})` : 'Workspace default'}</option>
                  {data?.profiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' ★' : ''}</option>
                  ))}
                </select>
              </label>
            ) : hasProfiles ? (
              <div className="t-micro muted">Connection: {effective?.name ?? 'None'}</div>
            ) : (
              <div className="t-micro muted">Demo showcase sync</div>
            )}

            <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => void sync()}>
              {busy ? 'Syncing…' : <>{I.confluence} Sync to Confluence</>}
            </button>

            {msg && (
              <div
                className="t-micro"
                style={{
                  color: msg.ok ? 'var(--ok, #128a4b)' : 'var(--err, #c0362c)',
                  maxHeight: 120, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: 'var(--surface-2, #f6f6f6)', borderRadius: 6, padding: '6px 8px',
                }}
              >
                {msg.ok ? '✓ ' : '✗ '}{msg.text}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

/**
 * Consolidated git-sync control: a single compact "Git" button in the header
 * that opens a bounded dropdown with the connection target picker, push/pull
 * actions, an "open PR" toggle, and last-commit / PR links. Keeps the header
 * uncluttered and confines any (long) error text to a fixed-width, wrapping
 * panel instead of overflowing the toolbar. Hidden when the workspace has no
 * git connections.
 */
function GitSyncControl({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const [profile, setProfile] = useState<ProjectRepoProfile | null>(null);
  const [status, setStatus] = useState<RepoStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openPr, setOpenPr] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const reload = React.useCallback(() => {
    getProjectRepoProfile(projectId).then(setProfile).catch(() => setProfile(null));
    getProjectRepoStatus(projectId).then(setStatus).catch(() => setStatus(null));
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    getProjectRepoProfile(projectId).then((d) => { if (alive) setProfile(d); }).catch(() => { if (alive) setProfile(null); });
    getProjectRepoStatus(projectId).then((s) => { if (alive) setStatus(s); }).catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, [projectId]);

  // No connections configured for this workspace → nothing to show.
  if (!profile || profile.profiles.length === 0) return null;

  const defaultProfile = profile.profiles.find((p) => p.isDefault);
  const effective = profile.profiles.find((p) => p.id === profile.effectiveProfileId);

  const onTargetChange = async (next: string): Promise<void> => {
    setBusy(true);
    try {
      const r = await setProjectRepoProfile(projectId, next || null);
      setProfile((d) => (d ? { ...d, selectedProfileId: r.selectedProfileId, effectiveProfileId: r.effectiveProfileId } : d));
      getProjectRepoStatus(projectId).then(setStatus).catch(() => {});
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message ?? 'Could not change target' });
    } finally {
      setBusy(false);
    }
  };

  const push = async (): Promise<void> => {
    setBusy(true); setMsg(null);
    try {
      const r = await syncProjectToRepo(projectId, { openPr });
      setMsg({ ok: true, text: r.changed === 0 ? 'Already up to date' : `Pushed ${r.changed} file${r.changed === 1 ? '' : 's'}${r.pr ? ` · PR #${r.pr.number}` : ''}` });
      reload();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message ?? 'Push failed' });
    } finally {
      setBusy(false);
    }
  };

  const pull = async (): Promise<void> => {
    if (!window.confirm('Pull from git? Changed artifacts will be saved as a new version.')) return;
    setBusy(true); setMsg(null);
    try {
      const r = await pullProjectFromRepo(projectId);
      setMsg({ ok: true, text: r.changed === 0 ? `Nothing changed${r.ignored ? ` · ${r.ignored} unmapped` : ''}` : `Pulled ${r.changed} artifact${r.changed === 1 ? '' : 's'} into a new version` });
      if (r.changed > 0) await useStore.getState().loadProject(projectId);
      reload();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message ?? 'Pull failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="btn btn--sm"
        onClick={() => setOpen((o) => !o)}
        title={status?.connected ? `Git: ${effective?.name ?? 'connected'}` : 'Git sync'}
      >
        {I.git} Git{status?.connected ? '' : ' •'}
      </button>
      {open && (
        <>
          {/* click-away backdrop */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 41,
              width: 320, maxWidth: '90vw', padding: 12, borderRadius: 10,
              background: 'var(--surface, #fff)', border: '1px solid var(--line, #e2e2e2)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div className="t-micro" style={{ fontWeight: 600 }}>Git sync</div>

            {/* Target */}
            {canEdit ? (
              <label className="t-micro muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Connection
                <select
                  className="select select--sm"
                  value={profile.selectedProfileId ?? ''}
                  disabled={busy}
                  onChange={(e) => void onTargetChange(e.target.value)}
                >
                  <option value="">{defaultProfile ? `Default (${defaultProfile.name})` : 'Workspace default'}</option>
                  {profile.profiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' ★' : ''}</option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="t-micro muted">Connection: {effective?.name ?? 'None'}</div>
            )}

            {/* Repo + last commit + PR links */}
            {status?.profile && (
              <div className="t-micro muted" style={{ wordBreak: 'break-word' }}>
                {status.profile.host}: {status.profile.workspace}/{status.profile.repo}
              </div>
            )}
            {status?.lastCommit?.url && (
              <a className="t-micro" href={status.lastCommit.url} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                Last commit ↗ {status.lastCommit.sha?.slice(0, 7)}
              </a>
            )}
            {status?.openPr?.url && (
              <a className="t-micro" href={status.openPr.url} target="_blank" rel="noreferrer">
                Open PR #{status.openPr.number} ↗
              </a>
            )}

            <label className="t-micro muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={openPr} onChange={(e) => setOpenPr(e.target.checked)} />
              Open a pull request on push
            </label>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => void push()} style={{ flex: 1 }}>
                {busy ? '…' : '↗ Push'}
              </button>
              <button className="btn btn--sm" disabled={busy} onClick={() => void pull()} style={{ flex: 1 }}>
                {busy ? '…' : '↙ Pull'}
              </button>
            </div>

            {msg && (
              <div
                className="t-micro"
                style={{
                  color: msg.ok ? 'var(--ok, #128a4b)' : 'var(--err, #c0362c)',
                  maxHeight: 120, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: 'var(--surface-2, #f6f6f6)', borderRadius: 6, padding: '6px 8px',
                }}
              >
                {msg.ok ? '✓ ' : '✗ '}{msg.text}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}
